import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import { useCCContext } from '../CC/CCContext';
import * as ccUtils from '../CC/utils';

const AnnotatedImageViewer = ({ imageName, annotationSource, width = 800, height = 600 }) => {
    const { t } = useTranslation();
    const { cc, setCc, first, setFirst, recordsPerPage, setRecordsPerPage, viewMode, setViewMode, browseMode, setBrowseMode, data, setData, isReady } = useCCContext();
    const ctx = useCCContext();

    // State to manage current image name and annotations
    const [currentImageName, setCurrentImageName] = useState(imageName || '');
    const [currentAnnotations, setCurrentAnnotations] = useState([]);

    const [hoveredAnnotation, setHoveredAnnotation] = useState(null);
    const [isMouseOverImage, setIsMouseOverImage] = useState(false);
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const containerRef = useRef(null);

    // Effect to handle annotationSource or imageName
    useEffect(() => {
        console.log("AnnotationSource:", annotationSource);
        console.log("ImageName:", imageName);
        if (annotationSource) {
            try {
                const parsed = JSON.parse(annotationSource);
                console.log("Parsed annotationSource:", parsed);
                if (parsed.imageName) {
                    setCurrentImageName(parsed.imageName);
                } else {
                    console.warn("Parsed annotationSource does not contain imageName.");
                    setCurrentImageName('');
                }
                if (Array.isArray(parsed.annotations)) {
                    setCurrentAnnotations(parsed.annotations);
                } else {
                    console.warn("Parsed annotationSource does not contain annotations array.");
                    setCurrentAnnotations([]);
                }
            } catch (error) {
                console.error("Failed to parse annotationSource.albumText:", error);
                setCurrentImageName('');
                setCurrentAnnotations([]);
            }
        } else if (imageName) {
            setCurrentImageName(imageName);
            // Fetch annotations since annotationSource is not provided
            const fetchAnnotations = async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}?action=getImageAnnotation&imageName=${encodeURIComponent(imageName)}`);
                    const data = await response.json();
                    setCurrentAnnotations(data.annotations || []);
                } catch (error) {
                    console.error("Failed to fetch annotations:", error);
                    setCurrentAnnotations([]);
                }
            };

            fetchAnnotations();
        } else {
            // Neither annotationSource nor imageName is provided
            setCurrentImageName('');
            setCurrentAnnotations([]);
        }
    }, [annotationSource, imageName]);

    // Function to draw annotations
    const drawAnnotations = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !isMouseOverImage) return;

        const ctxCanvas = canvas.getContext('2d');
        ctxCanvas.clearRect(0, 0, canvas.width, canvas.height);

        currentAnnotations.forEach((ann, index) => {
            drawAnnotation(ctxCanvas, ann, index, ann === hoveredAnnotation);
        });
    }, [currentAnnotations, hoveredAnnotation, isMouseOverImage]);

    // Function to update canvas size based on image size and container
    const updateCanvasSize = useCallback(() => {
        const image = imageRef.current;
        const canvas = canvasRef.current;
        const container = containerRef.current;

        if (image && canvas && container) {
            const containerWidth = container.offsetWidth;
            const containerHeight = container.offsetHeight;
            const scale = Math.min(containerWidth / image.naturalWidth, containerHeight / image.naturalHeight, 1);
            const scaledWidth = image.naturalWidth * scale;
            const scaledHeight = image.naturalHeight * scale;

            canvas.width = scaledWidth;
            canvas.height = scaledHeight;
            canvas.style.width = `${scaledWidth}px`;
            canvas.style.height = `${scaledHeight}px`;

            image.style.width = `${scaledWidth}px`;
            image.style.height = `${scaledHeight}px`;

            drawAnnotations();
        }
    }, [drawAnnotations]);

    const debouncedUpdateCanvasSize = useCallback(debounce(updateCanvasSize, 100), [updateCanvasSize]);

    // Effect to handle window resize and container resize
    useEffect(() => {
        const resizeObserver = new ResizeObserver(debouncedUpdateCanvasSize);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                resizeObserver.unobserve(containerRef.current);
            }
            debouncedUpdateCanvasSize.cancel();
        };
    }, [debouncedUpdateCanvasSize]);

    // Redraw annotations when dependencies change
    useEffect(() => {
        drawAnnotations();
    }, [drawAnnotations]);

    // Function to draw a single annotation
    const drawAnnotation = (ctxCanvas, ann, index, isHovered) => {
        const canvas = canvasRef.current;
        const image = imageRef.current;
        if (!canvas || !image) return;

        const scale = canvas.width / image.naturalWidth;

        ctxCanvas.strokeStyle = isHovered ? 'rgba(181, 52,29, 1.0)' : 'rgba(231,228, 223, 1.0)';
        ctxCanvas.lineWidth = isHovered ? 3 : 2;
        ctxCanvas.setLineDash(isHovered ? [] : [5, 3]);
        ctxCanvas.shadowColor = 'rgba(0, 0, 0, 0.6)';
        ctxCanvas.shadowBlur = isHovered ? 8 : 4;

        ctxCanvas.beginPath();
        ann.points.forEach((point, idx) => {
            const scaledX = point.x * scale;
            const scaledY = point.y * scale;
            if (idx === 0) ctxCanvas.moveTo(scaledX, scaledY);
            else ctxCanvas.lineTo(scaledX, scaledY);
        });
        if (ann.points.length > 2) {
            ctxCanvas.closePath();
        }
        ctxCanvas.stroke();
        ctxCanvas.setLineDash([]);
    };

    // Handle click on annotation
    const handleClick = (e, ann) => {
        const canvas = canvasRef.current;
        const image = imageRef.current;
        if (!canvas || !image) return;

        const scale = canvas.width / image.naturalWidth;

        const { x, y } = getMousePos(canvas, e);
        if (isPointInPolygon(x / scale, y / scale, ann)) {
            let currentMonument = "";
            if (Array.isArray(cc.facets['department']) && cc.facets['department'].length > 0) {
                currentMonument = cc.facets['department'][0];
            }
            const queryParams = new URLSearchParams(window.location.search);
            if (queryParams.get('albumexternal')) {
                window.open(`${window.location.origin}${window.location.pathname}?id=[${ann.text}]&index=1`, '_blank');
            } else {
                ccUtils.clearSearch(ctx);
                if (currentMonument !== "") {
                    ccUtils.addFacetValue(ctx, 'department', currentMonument);
                }
                ccUtils.addCustomSearch(ctx, '/record/objectnumber', t("record_objectnumber"), ['[' + ann.text + ']'], '');
                setViewMode('list');
            }
        }
    };

    // Handle mouse movement over the canvas
    const handleMouseMove = useCallback((e) => {
        const canvas = canvasRef.current;
        const image = imageRef.current;
        if (!canvas || !image) return;

        const scale = canvas.width / image.naturalWidth;
        const { x, y } = getMousePos(canvas, e);

        let hoveredAnn = null;
        for (let ann of currentAnnotations) {
            if (isPointInPolygon(x / scale, y / scale, ann)) {
                hoveredAnn = ann;
                break;
            }
        }

        if (hoveredAnn !== hoveredAnnotation) {
            setHoveredAnnotation(hoveredAnn);
        }
    }, [currentAnnotations, hoveredAnnotation]);

    // Handle mouse entering the canvas
    const handleMouseEnter = useCallback(() => {
        setIsMouseOverImage(true);
    }, []);

    // Handle mouse leaving the canvas
    const handleMouseLeave = useCallback(() => {
        setHoveredAnnotation(null);
        setIsMouseOverImage(false);
    }, []);

    // Add event listeners for mouse interactions
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseenter', handleMouseEnter);
        canvas.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseenter', handleMouseEnter);
            canvas.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [handleMouseMove, handleMouseEnter, handleMouseLeave]);

    // Add click event listener to the canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const handleCanvasClick = (e) => {
            currentAnnotations.forEach(ann => handleClick(e, ann));
        };

        canvas.addEventListener('click', handleCanvasClick);

        return () => {
            if (canvas) {
                canvas.removeEventListener('click', handleCanvasClick);
            }
        };
    }, [currentAnnotations]);

    // Function to get mouse position relative to the canvas
    const getMousePos = (canvas, evt) => {
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (evt.clientX - rect.left) * scaleX,
            y: (evt.clientY - rect.top) * scaleY
        };
    };

    // Function to check if a point is inside a polygon
    const isPointInPolygon = (x, y, polygon) => {
        let inside = false;
        for (let i = 0, j = polygon.points.length - 1; i < polygon.points.length; j = i++) {
            const xi = polygon.points[i].x, yi = polygon.points[i].y;
            const xj = polygon.points[j].x, yj = polygon.points[j].y;
            const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }
        return inside;
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                display: 'inline-block',
                width: '100%',
                height: '100%',
                maxWidth: width,
                maxHeight: height
            }}
        >
            {currentImageName ? (
                <img
                    ref={imageRef}
                    src={currentImageName}
                    alt="Annotatable"
                    style={{ display: 'block', width: '100%', height: '100%' }}
                    onLoad={updateCanvasSize}
                />
            ) : (
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f0f0f0'
                    }}
                >
                    {t("No image available")}
                </div>
            )}
            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    cursor: 'crosshair'
                }}
            />
        </div>
    );
};

export default AnnotatedImageViewer;
